import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api } from "../../api/client";
import { Send, Stethoscope, FileText, PlayCircle, CheckCircle2, Clock, User } from "lucide-react";

const AdminConsultationDetail = () => {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [msg, setMsg] = useState("");
    const [sending, setSending] = useState(false);
    const [completing, setCompleting] = useState(false);
    const [starting, setStarting] = useState(false);
    const [notes, setNotes] = useState("");
    const [rx, setRx] = useState("");
    const [followUp, setFollowUp] = useState(false);
    const listRef = useRef(null);

    const load = () => api.get(`/admin/consultations/${id}`).then((r) => setData(r.data)).finally(() => setLoading(false));
    useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { listRef.current && (listRef.current.scrollTop = listRef.current.scrollHeight); }, [data?.consultation?.messages?.length]);

    const send = async () => {
        if (!msg.trim()) return;
        setSending(true);
        try {
            await api.post(`/consultations/${id}/messages`, { content: msg.trim() });
            setMsg("");
            await load();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Send failed");
        } finally {
            setSending(false);
        }
    };

    const start = async () => {
        setStarting(true);
        try {
            await api.post(`/admin/consultations/${id}/start`);
            toast.success("Consultation started");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Could not start");
        } finally {
            setStarting(false);
        }
    };

    const complete = async () => {
        if (!notes.trim() && !rx.trim()) {
            toast.error("Add doctor notes or a prescription before completing.");
            return;
        }
        setCompleting(true);
        try {
            await api.post(`/admin/consultations/${id}/complete`, { doctor_notes: notes.trim(), follow_up_recommended: followUp, prescribed_rx_text: rx.trim() || null });
            toast.success("Consultation completed");
            setNotes(""); setRx(""); setFollowUp(false);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Could not complete");
        } finally {
            setCompleting(false);
        }
    };

    if (loading || !data) return (<AdminAuthGuard><AdminLayout title="Consultation"><p className="text-sm text-zinc-500">Loading…</p></AdminLayout></AdminAuthGuard>);
    const c = data.consultation;
    const doc = data.doctor;
    const patient = data.patient;

    const canStart = c.status === "confirmed";
    const canChat = ["confirmed", "in_progress"].includes(c.status);
    const canComplete = ["confirmed", "in_progress"].includes(c.status);

    return (
        <AdminAuthGuard>
            <AdminLayout title={`${doc?.full_name || "Consultation"}`} subtitle={`${(c.doctor_specialization || "").replace("-", " ")} • ${new Date(c.scheduled_at).toLocaleString()}`}>
                <Link to="/admin/consultations" className="text-xs text-zinc-500 hover:underline">← All consultations</Link>

                <div className="mt-3 flex items-center gap-2" data-testid="admin-consult-header">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${c.status === "completed" ? "bg-emerald-100 text-emerald-700" : c.status === "cancelled" ? "bg-rose-100 text-rose-700" : c.status === "in_progress" ? "bg-violet-100 text-violet-700" : "bg-blue-100 text-blue-700"}`}>
                        {(c.status || "").replace("_", " ")}
                    </span>
                    {canStart && (
                        <button onClick={start} disabled={starting} className="inline-flex items-center gap-1 rounded-md bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-700 disabled:opacity-50" data-testid="admin-consult-start-btn">
                            <PlayCircle className="h-3.5 w-3.5" /> {starting ? "Starting…" : "Start consult"}
                        </button>
                    )}
                </div>

                <div className="mt-6 grid gap-4 lg:grid-cols-3">
                    {/* Left: chat */}
                    <div className="lg:col-span-2 rounded-2xl border border-zinc-100 bg-white dark:bg-white/[0.03] dark:border-white/10">
                        <div ref={listRef} className="max-h-[60vh] min-h-[300px] space-y-3 overflow-y-auto p-5" data-testid="admin-consult-chat-thread">
                            <div className="rounded-xl bg-zinc-50 p-3 text-xs text-zinc-600">
                                <p className="font-medium text-zinc-900">Reason: {c.reason}</p>
                                {c.patient_notes && <p className="mt-1">{c.patient_notes}</p>}
                            </div>
                            {(c.messages || []).map((m) => {
                                const isDoctor = m.sender === "doctor";
                                return (
                                    <div key={m.id} className={`flex ${isDoctor ? "justify-end" : "justify-start"}`}>
                                        <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm ${isDoctor ? "rounded-br-sm bg-brand-700 text-white" : "rounded-bl-sm bg-zinc-100 text-zinc-900"}`}>
                                            <p className="text-[10px] opacity-70">{m.sender_name} · {new Date(m.at).toLocaleTimeString()}</p>
                                            <p className="whitespace-pre-line">{m.content}</p>
                                        </div>
                                    </div>
                                );
                            })}
                            {(c.messages || []).length === 0 && <p className="rounded-xl border border-dashed border-zinc-200 p-4 text-center text-xs text-zinc-500">No messages yet.</p>}
                        </div>
                        {canChat && (
                            <div className="border-t border-zinc-100 p-3">
                                <div className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        value={msg}
                                        onChange={(e) => setMsg(e.target.value)}
                                        onKeyDown={(e) => e.key === "Enter" && send()}
                                        placeholder="Reply to the patient as the doctor…"
                                        className="flex-1 rounded-full border border-zinc-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none"
                                        data-testid="admin-consult-message-input"
                                    />
                                    <button onClick={send} disabled={sending || !msg.trim()} className="rounded-full bg-brand-700 p-2.5 text-white hover:bg-brand-800 disabled:opacity-50" data-testid="admin-consult-message-send">
                                        <Send className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right: patient + complete form */}
                    <aside className="space-y-4">
                        <section className="rounded-2xl border border-zinc-100 bg-white p-4">
                            <p className="label-uppercase text-zinc-500">Patient</p>
                            <p className="mt-2 inline-flex items-center gap-1.5 font-medium text-zinc-900"><User className="h-4 w-4" />{patient?.full_name || patient?.email}</p>
                            <p className="text-xs text-zinc-500">{patient?.email}</p>
                            {patient?.phone && <p className="text-xs text-zinc-500">{patient.phone}</p>}
                        </section>

                        {doc && (
                            <section className="rounded-2xl border border-zinc-100 bg-white p-4">
                                <p className="label-uppercase text-zinc-500">Doctor</p>
                                <p className="mt-2 inline-flex items-center gap-1.5 font-medium text-zinc-900"><Stethoscope className="h-4 w-4 text-brand-700" />{doc.full_name}</p>
                                <p className="text-xs text-zinc-500 capitalize">{(doc.specialization || "").replace("-", " ")} • {doc.qualifications}</p>
                            </section>
                        )}

                        {canComplete && (
                            <section className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-4" data-testid="admin-consult-complete-card">
                                <p className="label-uppercase text-emerald-800">Close-out</p>
                                <label className="mt-2 block text-xs font-medium text-emerald-900">Doctor notes (visible to patient)</label>
                                <textarea rows={4} maxLength={4000} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Summary, diagnosis, next steps…" className="mt-1 w-full rounded-md border border-emerald-200 bg-white px-3 py-2 text-sm focus:border-emerald-400 focus:outline-none" data-testid="admin-consult-notes" />
                                <label className="mt-3 block text-xs font-medium text-emerald-900">Prescription (one med per line, optional)</label>
                                <textarea rows={3} maxLength={4000} value={rx} onChange={(e) => setRx(e.target.value)} placeholder={"e.g.\nAmoxicillin 500mg — 1 cap × 3 daily × 5 days\nParacetamol 500mg — SOS for fever"} className="mt-1 w-full rounded-md border border-emerald-200 bg-white px-3 py-2 text-sm focus:border-emerald-400 focus:outline-none" data-testid="admin-consult-rx" />
                                <label className="mt-3 flex items-center gap-2 text-xs text-emerald-900">
                                    <input type="checkbox" checked={followUp} onChange={(e) => setFollowUp(e.target.checked)} data-testid="admin-consult-followup" /> Recommend a follow-up
                                </label>
                                <button onClick={complete} disabled={completing} className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-emerald-700 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-800 disabled:opacity-50" data-testid="admin-consult-complete-btn">
                                    <CheckCircle2 className="h-4 w-4" /> {completing ? "Closing…" : "Mark as completed"}
                                </button>
                            </section>
                        )}

                        {c.status === "completed" && (
                            <section className="rounded-2xl border border-emerald-100 bg-emerald-50/30 p-4" data-testid="admin-consult-completed-summary">
                                <p className="label-uppercase text-emerald-800">Closed</p>
                                <p className="mt-2 text-xs text-emerald-900"><Clock className="mr-1 inline h-3 w-3" />Duration: {c.duration_minutes || "—"} min</p>
                                {c.doctor_notes && <p className="mt-2 whitespace-pre-line text-xs text-zinc-700">{c.doctor_notes}</p>}
                                {c.issued_prescription_id && (<p className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-brand-700"><FileText className="h-3 w-3" /> Prescription issued</p>)}
                            </section>
                        )}
                    </aside>
                </div>
            </AdminLayout>
        </AdminAuthGuard>
    );
};

export default AdminConsultationDetail;
